import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Scale, X, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useCompare } from "@/lib/compare";
import { api } from "@/lib/api";
import { FORMAT } from "@/lib/site";

export default function CompareTray() {
    const { ids, remove, clear, max } = useCompare();
    const [cars, setCars] = useState({});

    useEffect(() => {
        const missing = ids.filter((id) => !cars[id]);
        if (missing.length === 0) return;
        missing.forEach((id) => {
            api.get(`/cars/${id}`)
                .then((r) => setCars((c) => ({ ...c, [id]: r.data })))
                .catch(() => remove(id));
        });
    }, [ids, cars, remove]);

    if (ids.length === 0) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ y: 100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 100, opacity: 0 }}
                transition={{ type: "spring", stiffness: 220, damping: 26 }}
                className="fixed bottom-24 sm:bottom-5 left-4 right-4 sm:right-auto sm:left-6 z-[60] w-auto sm:max-w-[760px]"
                data-testid="compare-tray"
            >
                <div className="glass rounded-2xl border border-gold/30 shadow-soft p-3 sm:p-4 flex items-center gap-3 sm:gap-4">
                    <div className="hidden sm:flex w-11 h-11 rounded-full bg-gold/10 border border-gold/30 text-gold items-center justify-center shrink-0">
                        <Scale size={18} />
                    </div>

                    <div className="flex items-center gap-2 sm:gap-3 flex-1 overflow-x-auto">
                        {ids.map((id) => {
                            const c = cars[id];
                            return (
                                <div
                                    key={id}
                                    className="relative group flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-xl border border-line bg-surface/80 shrink-0 max-w-[220px]"
                                    data-testid={`compare-tray-item-${id}`}
                                >
                                    <div className="w-10 h-10 rounded-lg overflow-hidden bg-bg shrink-0">
                                        {c?.images?.[0] && (
                                            <img
                                                src={c.images[0]}
                                                alt=""
                                                className="w-full h-full object-cover"
                                            />
                                        )}
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-[11px] uppercase tracking-[0.2em] text-ink-subtle truncate">
                                            {c?.brand || "..."}
                                        </div>
                                        <div className="text-xs text-ink/90 truncate">
                                            {c?.model || "..."}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => remove(id)}
                                        className="ml-1 w-6 h-6 rounded-full text-ink-muted hover:text-red-400 flex items-center justify-center"
                                        aria-label="Премахни"
                                        data-testid={`compare-tray-remove-${id}`}
                                    >
                                        <X size={13} />
                                    </button>
                                </div>
                            );
                        })}

                        {ids.length < max && (
                            <span className="text-xs text-ink-subtle pl-1 shrink-0 hidden md:block">
                                Добавете още {max - ids.length} {max - ids.length === 1 ? "автомобил" : "автомобила"}
                            </span>
                        )}
                    </div>

                    <button
                        type="button"
                        onClick={clear}
                        className="text-[10px] uppercase tracking-[0.2em] text-ink-muted hover:text-gold hidden sm:block"
                        data-testid="compare-tray-clear"
                    >
                        Изчисти
                    </button>

                    {ids.length >= 2 ? (
                        <Link
                            to={`/compare?ids=${ids.join(",")}`}
                            className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-bg px-4 sm:px-5 py-2.5 rounded-full text-sm font-medium shrink-0 transition-all"
                            data-testid="compare-tray-go"
                        >
                            Сравни <ArrowRight size={14} />
                        </Link>
                    ) : (
                        <span className="text-xs text-ink-muted px-3 shrink-0 hidden sm:inline">
                            Минимум 2
                        </span>
                    )}
                </div>
            </motion.div>
        </AnimatePresence>
    );
}
